import { useForm as createUseForm } from "@ant-design-vue/use";
import { Col, notification, Row } from "ant-design-vue";
import lodash from "lodash";
import { defineAsyncComponent, reactive } from "vue";
import { addModal, setIsLoading as setIsLoadingApp } from "../../../app.logic";
import { setAppPageHeader } from "../../../components/layout/app-layout/app-layout.logic";
import i18n from "../../../i18n";
import store from "../../../store";
import { createButtonSave } from "../../../utils/button";
import { modalStyleForm } from "../../../utils/constant";
import { copyForm, initUseForm, watchRulesChildren } from "../../../utils/form";
import { createLoading } from "../../../utils/loading";
import { flatWhereGroup } from "../../../utils/search";
import { required, requiredWhen, validateError } from "../../../utils/validate";
import { createForm as createFormOfficer, createRules as createRulesOfficer } from "../../account/register-officer/register-officer.logic";
import { callUserRequestDoc, callUserRequestStatus, callUserRequestType } from "../company/mgt-tab/tab-4/tab4.section";
import { createConditionAction, createConditionForm } from "./profile.condition";
import { createField } from "./profile.section";

const ChangePassword = defineAsyncComponent(() => import("./ChangePassword"));

export const { isLoading, setIsLoading } = createLoading();

export const callRequest = ({ form }) => store.dispatch(`resource/v1/transaction/userRequest/request`, { data: form });
export const callMeProfile = () => store.dispatch(`resource/v1/security/user/meProfile`, {});

export const onClickRequest = ({ props, data, refs, callRequest }) => {
    setIsLoadingApp(true);
    return callRequest()
        .finally(() => setIsLoadingApp(false))
        .then(() => initForm({ props, data, refs }));
};

export const createForm = ({ props, data, refs }) => {
    return {
        model: null,
        refModelId: null,
        refModelTempId: null,
        refRequestType: null,
        refRequestDoc: null,
        refStatus: null,
        refDepartment: null,
        statusRemark: null,
        refUser: null,
        refUserTemp: { ...createFormOfficer({ props, data, refs }) },
        statuses: [],
        lastStatus: {
            forRequestRemark: [{ remark: null, statusDatetime: null }],
            forOfficeRemark: [{ remark: null, statusDatetime: null }]
        }
    };
};

export const createRules = ({ props, data, refs, key }) => {
    const prefix = key ? `${key}.` : "";
    return {
        ...createRulesOfficer({ props, data, refs, key: "refUserTemp" }),
        [`${prefix}refUserTemp.username`]: [],
        [`${prefix}refUserTemp.password`]: [],
        [`${prefix}refUserTemp.acceptCondition`]: [],
        [`${prefix}refUserTemp.idCard`]: [required]
    };
};

export const createRulesAttachment = ({ data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    const rules = {};
    model?.refUserTemp?.attachments?.forEach((item, index) => {
        if (item.isDelete !== true) {
            rules[`${prefix}refUserTemp.attachments.${index}.documentName`] = [required];
            rules[`${prefix}refUserTemp.attachments.${index}.size`] = [
                requiredWhen({ when: () => model.refUserTemp.attachments[index].attribute?.isRequiredAttachment })
            ];
        }
    });
    return rules;
};

export const createWhereFor = () => {
    const whereGroup = [];
    return [{ name: "refRequestDoc.key", type: "String", operator: "=", value: "7" }, ...flatWhereGroup({ where: whereGroup, logic: "OR" })];
};

export const loadForm = ({ props, data, refs }) => {
    setIsLoading(true);
    const { form } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    let promise = null;
    if (form) {
        promise = Promise.resolve({ data: [form] });
    } else {
        promise = callMeProfile({});
    }
    return promise
        .then(profile => {
            const refLastRequest7 = profile.refLastRequest7;
            if (refLastRequest7?.refStatus?.key === "waitApproveCenter") {
                initUseForm({ props, data, refs, page: { data: [{ ...refLastRequest7, refUser: profile }] } });
            } else {
                const profileTemp = copyForm({ form: profile });
                initUseForm({ props, data, refs, page: { data: [{ refOwner: profile, refUser: profile, refUserTemp: profileTemp }] } });
            }
        })
        .then(() => loadDefaultForm({ props, data, refs }))
        .then(() => initUseForm({ props, data, refs, page: { data: [modelRef] } }))
        .finally(() => setIsLoading(false));
};

export const loadDefaultForm = ({ data }) => {
    const { useForm } = data;
    const { modelRef } = useForm;
    const promises = [callUserRequestStatus({}), callUserRequestType({}), callUserRequestDoc({})];
    return Promise.all(promises).then(arr => {
        data.statuses = arr[0];
        data.userRequestTypes = arr[1];
        data.userRequestDocs = arr[2];

        if (!modelRef.id) {
            modelRef.refRequestType = data.userRequestTypes.find(d => d.key === "2");
            modelRef.refRequestDoc = data.userRequestDocs.find(d => d.key === "7");
        }
    });
};

export const initForm = ({ props, data, refs }) => {
    data.form = createForm({ props, data, refs });
    data.rules = createRules({ props, data, refs });
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs, isLoading });
    data.conditionForms = createConditionForm({ props, data, refs, isLoading });
    watchRulesChildren({
        props,
        data,
        refs,
        trigger: () => data.useForm.modelRef.refUserTemp.attachments.filter(d => d.isDelete !== true).length,
        createRules: () => createRulesAttachment({ props, data, refs }),
        itemsKey: "refUserTemp.attachments"
    });
    loadForm({ props, data, refs }).then(() => {
        if (props.setAppPageHeader) {
            setAppPageHeader({
                slots: {
                    default: () => createAppPageHeaderDefault({ props, data, refs })
                }
            });
        }
    });
};

export const createAppPageHeaderDefault = ({ props, data, refs }) => {
    const col1 = [];
    const col2 = [];
    const { useForm } = data;
    const { modelRef } = useForm;
    const canEdit = state.conditionAction.canEdit({ record: modelRef });
    if (canEdit) {
        col1.push(
            createButtonSave({
                onClick: () => {
                    data.useForm
                        .validate()
                        .then(() => {
                            const waitApproveCenterStatus = data.statuses.find(d => d.key === "waitApproveCenter");
                            return onClickRequest({
                                props,
                                data,
                                refs,
                                callRequest: () => callRequest({ form: { ...modelRef, refStatus: waitApproveCenterStatus } })
                            });
                        })
                        .catch(error => {
                            validateError({ error });
                            throw error;
                        });
                },
                classStyle: "m-1"
            })
        );
    }
    return (
        <Row type="flex">
            <Col>{col1}</Col>
            <Col class="ml-auto">{col2}</Col>
        </Row>
    );
};

export const openModalChangePassword = ({ data, refs }) => {
    const options = addModal({
        bind: lodash.merge(lodash.cloneDeep(modalStyleForm), {
            title: i18n.global.t("views.changePassword.title"),
            width: "520px",
            okText: i18n.global.t("button.changePassword"),
            onOk: () => {
                refs.changePassword.onSubmit().then(() => {
                    notification["success"]({ message: i18n.global.t("views.changePassword.success") });
                    options.bind.onCancel();
                });
            }
        }),
        render: () => (
            <ChangePassword
                ref={e => {
                    if (e) refs.changePassword = e;
                }}
                id={data.id}
            ></ChangePassword>
        )
    });
    return options;
};

export const state = reactive({
    conditionAction: createConditionAction()
});
